import { useMemo } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import { PIPELINE_WITH_PERMISSIONS } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { useFetchResolver } from 'reference-resolvers-lite/ResolverHooks';
import { SkeletonText } from 'reference-resolvers-lite/components/SkeletonText';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { useHttpClient } from '../../../client/HttpClientContext';
import { useObjectTypeId } from '../../../v2/hooks/useObjectTypeId';
import { useAdditionalPropertyValues } from '../../../v2/hooks/useAdditionalPropertyValues';
import { getPipelinePropertyName, getPipelineStagePropertyName } from '../utils/pipelineUtils';
import PipelineModal from './PipelineModal';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getDataAttributes } from '../../../utils/getDataAttributes';
import { useStageChangeValidation } from '../hooks/useStageChangeValidation';
import { isLoading } from 'reference-resolvers-lite/Status';
import { usePropertyValue } from '../../../v2/hooks/usePropertyValue';
import { getTransferrableProps } from '../../../utils/getTransferrableProps';
import { useIsUngatedForDisplayOptimizedFormControl } from '../../../v2/hooks/useIsUngatedForDisplayOptimizedFormControl';
import { Button } from 'foundations-components/transitional/components/Button';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PipelineButton = props => {
  const {
    autoFocus,
    readOnly,
    disabled,
    onChangeWithStageValidation,
    popoverOpen,
    setPopoverOpen,
    use = 'form',
    truncate = false
  } = props;
  const objectTypeId = useObjectTypeId();
  const httpClient = useHttpClient();
  const value = usePropertyValue();
  const isUngatedForDisplayOptimizedFormControl = useIsUngatedForDisplayOptimizedFormControl();
  const pipelineResolver = createStandardCachedResolver(PIPELINE_WITH_PERMISSIONS, objectTypeId, {
    httpClient
  });
  const {
    status,
    reference
  } = useFetchResolver(pipelineResolver, value === null ? undefined : value);
  const buttonDisplayText = useMemo(() => {
    if (isLoading(status)) {
      return /*#__PURE__*/_jsx(SkeletonText, {
        "data-test-id": "loading-text"
      });
    }
    return (reference === null || reference === void 0 ? void 0 : reference.label) || value || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataProperties.PropertyInputPipelineV2.placeholder"
    });
  }, [status, reference, value]);
  const {
    data: additionalValues,
    loading: additionalValuesLoading
  } = useAdditionalPropertyValues({
    pipelineStageId: getPipelineStagePropertyName(objectTypeId)
  });
  if (additionalValuesLoading || additionalValues === undefined) {
    return null;
  }
  const {
    pipelineStageId
  } = additionalValues;
  const defaultPipelineId = value === undefined ? null : value;
  const defaultPipelineStageId = pipelineStageId === undefined ? null : pipelineStageId;
  const handleClick = () => setPopoverOpen(true);
  const handleCancel = () => setPopoverOpen(false);
  const handleConfirm = ({
    pipelineId: newPipelineId,
    pipelineStageId: newPipelineStageId,
    stageReference
  }) => {
    setPopoverOpen(false);
    if (newPipelineId === defaultPipelineId && newPipelineStageId === defaultPipelineStageId) {
      return;
    }
    const changes = {
      [getPipelinePropertyName(objectTypeId)]: newPipelineId,
      [getPipelineStagePropertyName(objectTypeId)]: newPipelineStageId
    };
    onChangeWithStageValidation(changes, stageReference);
  };
  const ButtonComponent = isUngatedForDisplayOptimizedFormControl ? Button : UIButton;

  // trellis typescript cleanup- Button['use'] doesn't include 'view-group', UIButton['use'] dose not
  const buttonUse = use === 'view-group' ? 'form' : use;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ButtonComponent, Object.assign({}, getDataAttributes(props), getTransferrableProps(props, ['onChange']), {
      autoFocus: autoFocus,
      disabled: readOnly || disabled,
      onClick: handleClick,
      use: buttonUse,
      truncate: truncate,
      children: buttonDisplayText
    })), popoverOpen && /*#__PURE__*/_jsx(PipelineModal, Object.assign({
      onConfirm: handleConfirm,
      onCancel: handleCancel,
      defaultPipelineId: defaultPipelineId,
      defaultPipelineStageId: defaultPipelineStageId
    }, props))]
  });
};
PipelineButton.displayName = 'PipelineButton';
const PipelineButtonWrapper = props => {
  const {
    onChangeMultiple,
    inputMode
  } = props;
  const {
    stageChangeValidatorLoading,
    stageValidationModal,
    onChangeWithStageValidation
  } = useStageChangeValidation({
    onChangeMultiple,
    inputMode
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PipelineButton, Object.assign({}, props, {
      onChangeWithStageValidation: onChangeWithStageValidation,
      stageChangeValidatorLoading: stageChangeValidatorLoading
    })), stageValidationModal]
  });
};
export default PipelineButtonWrapper;